/* eslint-disable camelcase */
import { useState, useEffect, useRef } from 'react';

import { showMaintenanceBanner } from 'actions/settings';
import { isAuthenticated } from 'actions/user';
import { ONE_HOUR, TWENTY_FOUR_HOURS } from 'constants/constants';
import { TIME_FORMATS } from 'constants/timeformats';
import { dateFormat, getDateTime, toLocal, toUTC } from 'helpers/day';
import {
  convertTimeToMilliseconds,
  unixTimeDiffInSeconds,
  compareDates,
} from 'helpers/utils';
import { settingsSelector, userSelector } from 'selectors/otherSelectors';
import { useAppSelector, useAppDispatch } from 'storeHooks';

const useMaintenance = () => {
  const settings = useAppSelector(settingsSelector);
  const user = useAppSelector(userSelector);
  const isLoggedIn = isAuthenticated(user);
  const dispatch = useAppDispatch();

  const [showBanner, setShowBanner] = useState(false);
  const [isVisibleToUsers, setVisibleToUsers] = useState(false);
  const {
    maintenance_start_time,
    maintenance_finish_time,
    maintenance_announcement_time,
  } = settings;

  const timerRef: { current: NodeJS.Timeout | null } = useRef(null);
  const countdownRef: { current: number | null } = useRef(null);

  const maintenanceStartTimeInMilliseconds = convertTimeToMilliseconds(
    Number(maintenance_start_time)
  );
  const timeLeftToStartMaintenance = unixTimeDiffInSeconds(
    maintenanceStartTimeInMilliseconds
  );

  const [countdownTimer, setCountdownTimer] = useState(timeLeftToStartMaintenance);
  countdownRef.current = countdownTimer;

  const maintenanceFinishTimeInMilliseconds = convertTimeToMilliseconds(
    Number(maintenance_finish_time)
  );
  const maintenanceStartTime = dateFormat(
    toLocal(toUTC(getDateTime(maintenanceStartTimeInMilliseconds))),
    `${TIME_FORMATS.DD_MMM} ${TIME_FORMATS.LT}`
  );

  const maintenanceFinishTime = dateFormat(
    toLocal(toUTC(getDateTime(maintenanceFinishTimeInMilliseconds))),
    `${TIME_FORMATS.DD_MMM} ${TIME_FORMATS.LT}`
  );

  const isDatesEqual = compareDates(
    maintenanceStartTimeInMilliseconds,
    maintenanceFinishTimeInMilliseconds
  );

  const maintenanceDuration = unixTimeDiffInSeconds(
    maintenanceFinishTimeInMilliseconds,
    maintenanceStartTimeInMilliseconds
  );

  useEffect(() => {
    setCountdownTimer(timeLeftToStartMaintenance);
  }, [timeLeftToStartMaintenance]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      if (countdownRef.current && countdownRef.current <= 0) {
        clearInterval(timerRef.current!);
        setShowBanner(false);
      } else {
        setCountdownTimer(state => state! - 1);
      }
    }, 1000);
    return () => clearInterval(timerRef.current!);
  }, [maintenance_start_time]);

  useEffect(() => {
    if (
      !isLoggedIn &&
      maintenanceDuration &&
      maintenanceDuration > 4 * ONE_HOUR &&
      countdownRef.current &&
      countdownRef.current > 0 &&
      countdownRef.current <= TWENTY_FOUR_HOURS
    ) {
      setVisibleToUsers(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, countdownTimer]);

  useEffect(() => {
    dispatch(showMaintenanceBanner(showBanner));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBanner]);

  return {
    isLoggedIn,
    showBanner,
    setShowBanner,
    maintenanceStartTime,
    maintenanceFinishTime,
    isDatesEqual,
    maintenanceDuration,
    timeLeftToStartMaintenance,
    maintenance_announcement_time,
    maintenanceFinishTimeInMilliseconds,
    isVisibleToUsers,
    maintenance_start_time,
    maintenance_finish_time,
    countdownTimer,
    countdownRef,
  };
};

export default useMaintenance;
